import React from 'react';
import Icon from '../../../components/common/Icon/Icon';
import { formatCurrency, formatDate, formatDateTime } from '../../../utils';
import type { CheckoutPaymentInstructions } from '../checkout.types';
import BoletoBarCodeDisplay from './BoletoBarCodeDisplay';
import CheckoutCopyField from './CheckoutCopyField';
import styles from './CheckoutPaymentInstructionsPanel.module.css';

interface CheckoutPaymentInstructionsPanelProps {
  instructions: CheckoutPaymentInstructions;
}

const CheckoutPaymentInstructionsPanel: React.FC<CheckoutPaymentInstructionsPanelProps> = ({
  instructions,
}) => {
  const isPix = instructions.method === 'pix';

  return (
    <section className={styles.panel} aria-label={isPix ? 'Pagamento via PIX' : 'Pagamento via boleto'}>
      <header className={styles.header}>
        <span className={styles.iconWrap} aria-hidden>
          <Icon
            name={isPix ? 'qr_code_2' : 'receipt_long'}
            size={28}
            color="var(--color-brand-primary)"
          />
        </span>
        <div className={styles.headerText}>
          <h3 className={styles.title}>
            {isPix ? 'Pague com PIX' : 'Pague com boleto'}
          </h3>
          <p className={styles.subtitle}>
            {isPix
              ? 'Escaneie o QR Code no app do seu banco ou copie o código abaixo.'
              : 'Copie a linha digitável ou use o código de barras para pagar no app ou internet banking.'}
          </p>
        </div>
      </header>

      <div className={styles.metaRow}>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Valor</span>
          <span className={styles.metaValue}>{formatCurrency(instructions.value)}</span>
        </div>
        {instructions.dueDate && (
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Vencimento</span>
            <span className={styles.metaValue}>
              {instructions.dueDateTime ? formatDateTime(instructions.dueDateTime) : formatDate(instructions.dueDate)}
            </span>
          </div>
        )}
        {isPix && instructions.expirationDate && (
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>PIX expira em</span>
            <span className={styles.metaValue}>
              {instructions.expirationDate.length > 10
                ? formatDateTime(instructions.expirationDate)
                : formatDate(instructions.expirationDate)}
            </span>
          </div>
        )}
      </div>

      {isPix ? (
        <>
          {instructions.qrCodeImageBase64 && (
            <div className={styles.qrWrap}>
              <img
                src={`data:image/png;base64,${instructions.qrCodeImageBase64}`}
                alt="QR Code PIX para pagamento"
                className={styles.qrImage}
              />
            </div>
          )}
          {instructions.copyPasteCode && (
            <CheckoutCopyField
              label="PIX copia e cola"
              value={instructions.copyPasteCode}
              copyLabel="Copiar código PIX"
              multiline
            />
          )}
        </>
      ) : (
        <>
          {instructions.barCode && <BoletoBarCodeDisplay barCode={instructions.barCode} />}
          {instructions.identificationField && (
            <CheckoutCopyField
              label="Linha digitável"
              value={instructions.identificationField}
              copyLabel="Copiar linha digitável"
            />
          )}
          {instructions.barCode && (
            <CheckoutCopyField
              label="Código de barras"
              value={instructions.barCode}
              copyLabel="Copiar código de barras"
            />
          )}
          {instructions.bankSlipUrl && (
            <a
              href={instructions.bankSlipUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.bankSlipLink}
            >
              <Icon name="open_in_new" size={16} aria-hidden />
              Abrir boleto em PDF
            </a>
          )}
        </>
      )}

      <p className={styles.notice}>
        Após o pagamento, a confirmação pode levar alguns minutos. Você receberá a confirmação por e-mail.
      </p>
    </section>
  );
};

export default CheckoutPaymentInstructionsPanel;
